import getError from "@features/utils/get-error";
import {
  FollowState,
  LabFollowResponse,
  UserFollowResponse
} from "@interfaces/follow-types";
import {
  createAsyncThunk,
  createSelector,
  createSlice
} from "@reduxjs/toolkit";
import { enqueueSnackbar } from "notistack";
import api, { cancelToken } from "src/services/api";
import { RootState } from "src/services/store";

const initialState: FollowState = {
  lab: {
    isFollowing: false,
    loading: {
      followStatus: false,
      followAction: false,
      unfollowAction: false
    }
  },
  user: {
    isFollowing: false,
    loading: {
      followStatus: false,
      followAction: false,
      unfollowAction: false
    }
  },
  error: null
};

// Lab Follow Thunks
export const followLab = createAsyncThunk(
  "follow/followLab",
  async (labId: string, { signal, rejectWithValue }) => {
    const source = cancelToken.source();
    signal.addEventListener("abort", () => source.cancel());

    try {
      const response = await api.post(`/follow/lab/${labId}`, null, {
        cancelToken: source.token
      });

      enqueueSnackbar("Successfully followed lab", { variant: "success" });
      return response.data;
    } catch (error) {
      return rejectWithValue(getError(error));
    }
  },
  {
    condition: (labId) => {
      return !!labId;
    }
  }
);

export const unfollowLab = createAsyncThunk(
  "follow/unfollowLab",
  async (labId: string, { signal, rejectWithValue }) => {
    const source = cancelToken.source();
    signal.addEventListener("abort", () => source.cancel());

    try {
      const response = await api.delete(`/follow/lab/${labId}`, {
        cancelToken: source.token
      });

      enqueueSnackbar("Successfully unfollowed lab", { variant: "success" });
      return response.data;
    } catch (error) {
      return rejectWithValue(getError(error));
    }
  },
  {
    condition: (labId) => {
      return !!labId;
    }
  }
);

export const getLabFollowStatus = createAsyncThunk(
  "follow/getLabStatus",
  async (labId: string, { signal, rejectWithValue }) => {
    const source = cancelToken.source();
    signal.addEventListener("abort", () => source.cancel());

    try {
      const response = await api.get<LabFollowResponse>(
        `/follow/lab/${labId}/status`,
        {
          cancelToken: source.token
        }
      );

      return response.data.data;
    } catch (error) {
      return rejectWithValue(getError(error));
    }
  },
  {
    condition: (labId, { getState }) => {
      const state = getState() as RootState;
      if (!state.auth.user.id) {
        return false;
      }

      return !!labId;
    }
  }
);

// User Follow Thunks
export const followUser = createAsyncThunk(
  "follow/followUser",
  async (userId: string, { signal, rejectWithValue }) => {
    const source = cancelToken.source();
    signal.addEventListener("abort", () => source.cancel());

    try {
      const response = await api.post(`/follow/user/${userId}`, null, {
        cancelToken: source.token
      });

      enqueueSnackbar("Successfully followed user", { variant: "success" });
      return response.data;
    } catch (error) {
      return rejectWithValue(getError(error));
    }
  },
  {
    condition: (userId) => {
      return !!userId;
    }
  }
);

export const unfollowUser = createAsyncThunk(
  "follow/unfollowUser",
  async (userId: string, { signal, rejectWithValue }) => {
    const source = cancelToken.source();
    signal.addEventListener("abort", () => source.cancel());

    try {
      const response = await api.delete(`/follow/user/${userId}`, {
        cancelToken: source.token
      });

      enqueueSnackbar("Successfully unfollowed user", { variant: "success" });
      return response.data;
    } catch (error) {
      return rejectWithValue(getError(error));
    }
  },
  {
    condition: (userId) => {
      return !!userId;
    }
  }
);

export const getUserFollowStatus = createAsyncThunk(
  "follow/getUserStatus",
  async (userId: string, { signal, rejectWithValue }) => {
    const source = cancelToken.source();
    signal.addEventListener("abort", () => source.cancel());

    try {
      const response = await api.get<UserFollowResponse>(
        `/follow/user/${userId}/status`,
        {
          cancelToken: source.token
        }
      );

      return response.data.data;
    } catch (error) {
      return rejectWithValue(getError(error));
    }
  },
  {
    condition: (userId, { getState }) => {
      const state = getState() as RootState;
      if (!state.auth.user.id) {
        return false;
      }

      return !!userId;
    }
  }
);

// Slice
export const followSlice = createSlice({
  name: "follow",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Lab Follow
      .addCase(followLab.pending, (state) => {
        state.lab.loading.followAction = true;
        state.error = null;
      })
      .addCase(followLab.fulfilled, (state) => {
        state.lab.isFollowing = true;
        state.lab.loading.followAction = false;
      })
      .addCase(followLab.rejected, (state, action) => {
        state.lab.loading.followAction = false;
        state.error = action.payload as string;
      })

      // Lab Unfollow
      .addCase(unfollowLab.pending, (state) => {
        state.lab.loading.unfollowAction = true;
        state.error = null;
      })
      .addCase(unfollowLab.fulfilled, (state) => {
        state.lab.isFollowing = false;
        state.lab.loading.unfollowAction = false;
      })
      .addCase(unfollowLab.rejected, (state, action) => {
        state.lab.loading.unfollowAction = false;
        state.error = action.payload as string;
      })

      // Lab Follow Status
      .addCase(getLabFollowStatus.pending, (state) => {
        state.lab.loading.followStatus = true;
        state.error = null;
      })
      .addCase(getLabFollowStatus.fulfilled, (state, action) => {
        state.lab.isFollowing = action.payload?.isFollowing || false;
        state.lab.loading.followStatus = false;
      })
      .addCase(getLabFollowStatus.rejected, (state, action) => {
        state.lab.loading.followStatus = false;
        state.error = action.payload as string;
      })

      // User Follow
      .addCase(followUser.pending, (state) => {
        state.user.loading.followAction = true;
        state.error = null;
      })
      .addCase(followUser.fulfilled, (state) => {
        state.user.isFollowing = true;
        state.user.loading.followAction = false;
      })
      .addCase(followUser.rejected, (state, action) => {
        state.user.loading.followAction = false;
        state.error = action.payload as string;
      })

      // User Unfollow
      .addCase(unfollowUser.pending, (state) => {
        state.user.loading.unfollowAction = true;
        state.error = null;
      })
      .addCase(unfollowUser.fulfilled, (state) => {
        state.user.isFollowing = false;
        state.user.loading.unfollowAction = false;
      })
      .addCase(unfollowUser.rejected, (state, action) => {
        state.user.loading.unfollowAction = false;
        state.error = action.payload as string;
      })

      // User Follow Status
      .addCase(getUserFollowStatus.pending, (state) => {
        state.user.loading.followStatus = true;
        state.error = null;
      })
      .addCase(getUserFollowStatus.fulfilled, (state, action) => {
        state.user.isFollowing = action.payload?.isFollowing || false;
        state.user.loading.followStatus = false;
      })
      .addCase(getUserFollowStatus.rejected, (state, action) => {
        state.user.loading.followStatus = false;
        state.error = action.payload as string;
      });
  }
});

// Actions
export const { clearError } = followSlice.actions;

// Selectors
const getSlice = (state: RootState) => state.follow;

// Lab follow selectors
export const selectLabIsFollowing = createSelector(
  getSlice,
  (slice) => slice.lab.isFollowing
);
export const selectLabFollowLoading = createSelector(
  getSlice,
  (slice) => slice.lab.loading
);

// User follow selectors
export const selectUserIsFollowing = createSelector(
  getSlice,
  (slice) => slice.user.isFollowing
);
export const selectUserFollowLoading = createSelector(
  getSlice,
  (slice) => slice.user.loading
);

// Common selectors
export const selectFollowError = createSelector(
  getSlice,
  (slice) => slice.error
);

export default followSlice.reducer;
